import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Trash2, Mail, Check, Clock, RefreshCw, Loader2, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSuggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [processing, setProcessing] = useState(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/suggestions`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const handleMarkRead = async (id) => {
    setProcessing(id);
    try {
      const response = await fetch(`${API_URL}/api/suggestions/${id}/read`, {
        method: 'PUT'
      });
      if (response.ok) {
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    
    setProcessing(id);
    try {
      const response = await fetch(`${API_URL}/api/suggestions/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Feedback deleted');
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setProcessing(null);
    }
  };

  const filteredSuggestions = suggestions.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !s.read;
    if (filter === 'read') return s.read;
    return true;
  });

  const unreadCount = suggestions.filter(s => !s.read).length;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-suggestions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif text-charcoal flex items-center gap-3">
            <MessageSquare className="text-burgundy" />
            Private Suggestions
            {unreadCount > 0 && (
              <span className="px-2 py-1 bg-burgundy text-white text-sm rounded-full">
                {unreadCount} new
              </span>
            )}
          </h1>
          <p className="text-charcoal/60 text-sm mt-1">
            Private feedback from visitors (only visible to you)
          </p>
        </div>
        <button
          onClick={fetchSuggestions}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cream hover:bg-gold/20 transition-colors text-sm"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-amber-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{unreadCount}</div>
          <div className="text-xs text-amber-800">Unread</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{suggestions.length - unreadCount}</div>
          <div className="text-xs text-green-800">Read</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gold/10 pb-4">
        {['all', 'unread', 'read'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f 
                ? 'bg-burgundy text-white' 
                : 'bg-cream text-charcoal hover:bg-gold/20'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'unread' && unreadCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Suggestions List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-burgundy" size={32} />
        </div>
      ) : filteredSuggestions.length === 0 ? (
        <div className="text-center py-20 bg-cream/50 rounded-2xl">
          <MessageSquare size={48} className="mx-auto text-charcoal/30 mb-4" />
          <p className="text-charcoal/60">
            {filter === 'unread' ? 'No unread feedback' : 'No feedback yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSuggestions.map((suggestion) => (
            <motion.div
              key={suggestion.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl border p-6 transition-all ${
                suggestion.read 
                  ? 'border-gold/10' 
                  : 'border-burgundy/30 shadow-md'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Unread Indicator */}
                <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                  suggestion.read ? 'bg-gray-200' : 'bg-burgundy'
                }`} />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="font-medium text-charcoal">
                      {suggestion.name}
                    </span>
                    {!suggestion.read && (
                      <span className="px-2 py-0.5 bg-burgundy/10 text-burgundy text-xs rounded-full flex items-center gap-1">
                        <Clock size={10} />
                        New
                      </span>
                    )}
                    <span className="text-xs text-charcoal/50">
                      {formatDate(suggestion.created_at)}
                    </span>
                  </div>

                  {/* Message */}
                  <div className="bg-cream/50 rounded-lg p-4 mb-3">
                    <p className="text-charcoal/80 whitespace-pre-wrap">
                      {suggestion.message}
                    </p>
                  </div>

                  {/* Email if provided */}
                  {suggestion.email && (
                    <a 
                      href={`mailto:${suggestion.email}`}
                      className="inline-flex items-center gap-2 text-sm text-burgundy hover:underline"
                    >
                      <Mail size={14} />
                      {suggestion.email}
                    </a>
                  )}

                  {/* Admin Notes */}
                  {suggestion.admin_notes && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <div className="flex items-center gap-2 text-xs text-amber-700 mb-1">
                        <StickyNote size={12} />
                        Your Notes
                      </div>
                      <p className="text-sm text-amber-800">{suggestion.admin_notes}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {!suggestion.read && (
                    <button
                      onClick={() => handleMarkRead(suggestion.id)}
                      disabled={processing === suggestion.id}
                      className="p-2 rounded-lg bg-green-100 hover:bg-green-200 transition-colors disabled:opacity-50"
                      title="Mark as Read"
                    >
                      {processing === suggestion.id ? (
                        <Loader2 size={18} className="animate-spin text-green-600" />
                      ) : (
                        <Check size={18} className="text-green-600" />
                      )}
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDelete(suggestion.id)}
                    disabled={processing === suggestion.id}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
